import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { useI18n } from '../../../i18n'
import type { AdminAccount } from '../../../types'
import {
  requestTwoFactorRecoveryAcknowledge,
  requestTwoFactorFactorVerify,
  requestTwoFactorDisable,
  requestTwoFactorRecoveryRegenerate,
  requestTwoFactorSetup,
  requestTwoFactorVerify,
  type TwoFactorSetupPayload,
  type TwoFactorVerifyPayload,
} from '../twoFactorApi'
import type { AdminHeadersGetter } from './types'
import { runAsyncAction } from '../asyncActionFeedback'

type Params = {
  token: string | null
  authHeaders: AdminHeadersGetter
  t: ReturnType<typeof useI18n>['t']
  setAccount: Dispatch<SetStateAction<AdminAccount | null>>
  ensureAuthenticated: () => boolean
}

export const useProfileSecurityTwoFactorActions = ({
  token,
  authHeaders,
  t,
  setAccount,
  ensureAuthenticated,
}: Params) => {
  const verifyTwoFactorFactor = useCallback(
    async (
      factorCode: string,
    ): Promise<{ valid: boolean; factorType: 'otp' | 'recovery' | null }> => {
      if (!token) return { valid: false, factorType: null }
      try {
        return await requestTwoFactorFactorVerify({ authHeaders, factorCode, t })
      } catch {
        return { valid: false, factorType: null }
      }
    },
    [authHeaders, t, token],
  )

  const setupTwoFactor = useCallback(async (): Promise<TwoFactorSetupPayload | null> => {
    if (!ensureAuthenticated()) return null
    const data = await requestTwoFactorSetup({ authHeaders, t })
    if (data.account) {
      setAccount(data.account as AdminAccount)
    }
    toast.success(t('profile.security.twoFactor.toast.setupStarted'))
    return data.setup ?? null
  }, [authHeaders, ensureAuthenticated, setAccount, t])

  const verifyTwoFactor = useCallback(
    async (code: string): Promise<TwoFactorVerifyPayload | null> => {
      if (!ensureAuthenticated()) return null
      const data = await runAsyncAction({
        action: () => requestTwoFactorVerify({ authHeaders, code, t }),
        fallbackErrorMessage: t('profile.security.twoFactor.error.verify'),
        onErrorMessage: (message) => toast.error(message),
      })
      if (!data) return null

      if (data.account) {
        setAccount(data.account as AdminAccount)
      }
      toast.success(t('profile.security.twoFactor.toast.enabled'))
      return data.twoFactor ?? null
    },
    [authHeaders, ensureAuthenticated, setAccount, t],
  )

  const disableTwoFactor = useCallback(
    async (input: { code?: string; recoveryCode?: string }) => {
      if (!ensureAuthenticated()) return false
      const data = await runAsyncAction({
        action: () => requestTwoFactorDisable({ authHeaders, input, t }),
        fallbackErrorMessage: t('profile.security.twoFactor.error.disable'),
        onErrorMessage: (message) => toast.error(message),
      })
      if (!data) return false

      if (data.account) {
        setAccount(data.account as AdminAccount)
      }
      toast.success(t('profile.security.twoFactor.toast.disabled'))
      return true
    },
    [authHeaders, ensureAuthenticated, setAccount, t],
  )

  const regenerateTwoFactorRecoveryCodes = useCallback(
    async (code: string): Promise<TwoFactorVerifyPayload | null> => {
      if (!ensureAuthenticated()) return null
      const data = await runAsyncAction({
        action: () => requestTwoFactorRecoveryRegenerate({ authHeaders, code, t }),
        fallbackErrorMessage: t('profile.security.twoFactor.error.recoveryRegenerate'),
        onErrorMessage: (message) => toast.error(message),
      })
      if (!data) return null

      if (data.account) {
        setAccount(data.account as AdminAccount)
      }
      toast.success(t('profile.security.twoFactor.toast.recoveryRegenerated'))
      return data.twoFactor ?? null
    },
    [authHeaders, ensureAuthenticated, setAccount, t],
  )

  const acknowledgeTwoFactorRecoveryCodes = useCallback(
    async () => {
      if (!ensureAuthenticated()) return false
      const data = await runAsyncAction({
        action: () => requestTwoFactorRecoveryAcknowledge({ authHeaders, t }),
        fallbackErrorMessage: t('profile.security.twoFactor.error.recoveryAcknowledge'),
        onErrorMessage: (message) => toast.error(message),
      })
      if (!data) return false

      if (data.account) {
        setAccount(data.account as AdminAccount)
      }
      toast.success(t('profile.security.twoFactor.toast.recoveryAcknowledged'))
      return true
    },
    [authHeaders, ensureAuthenticated, setAccount, t],
  )

  return {
    verifyTwoFactorFactor,
    setupTwoFactor,
    verifyTwoFactor,
    disableTwoFactor,
    regenerateTwoFactorRecoveryCodes,
    acknowledgeTwoFactorRecoveryCodes,
  }
}
